/**
 * FIX-LOGOUT-ON-REFRESH-1 — cold-boot session restore.
 *
 * Access + refresh JWTs live in module memory. A full page load wipes them.
 * Boot must try cookie-based (or body) refresh once when /auth/me yields no user,
 * then re-fetch /auth/me. Never retry refresh on failure (no loop).
 *
 * Intentionally lives in AuthContext's boot layer — not the axios /auth/me 401
 * short-circuit (lib/api.ts), which still returns null before interceptor refresh.
 * /auth/me often returns 200 { user: null } with no token; only AuthContext can
 * cover that path. Keep the interceptor short-circuit so anonymous /auth/me probes
 * do not hard-redirect via the generic 401→refresh→login path.
 */

export type ColdBootSessionDeps<T> = {
  fetchMe: () => Promise<T | null>;
  refreshSession: () => Promise<void>;
};

/**
 * Cold-boot hydrate: /auth/me → if null, refreshSession once → /auth/me again.
 * On refresh failure: return null immediately (caller → /login). No retry loop.
 */
export async function coldBootRestoreSession<T>(
  deps: ColdBootSessionDeps<T>,
): Promise<T | null> {
  const first = await deps.fetchMe();
  if (first) {
    return first;
  }

  try {
    await deps.refreshSession();
  } catch {
    return null;
  }

  return deps.fetchMe();
}
